import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../colors";
import DarkHeroPageEffects from "../DarkHeroPageEffects";
import { PageContainer } from "../PageContainer";

const MotionBox = motion(Box);

const HERO_BG = "#080808";

const heroSectionSx = {
  position: "relative",
  overflow: "hidden",
  backgroundColor: HERO_BG,
  px: { xs: 2, md: 4 },
  minHeight: "100svh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  pb: { xs: "80px", md: "96px" },
};

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const REVEAL_TRANSITION = { duration: 0.9, ease: [0.22, 1, 0.36, 1] };

const HERO_ENTER = {
  initial: { y: 26, opacity: 0, filter: "blur(10px)" },
  animate: { y: 0, opacity: 1, filter: "blur(0px)" },
};

const sectionSx = {
  py: { xs: "80px", md: "120px" },
  px: { xs: 2, md: 4 },
  borderTop: `1px solid ${colors.border.subtle}`,
  backgroundColor: HERO_BG,
};

const cardSx = {
  background: `
    linear-gradient(
      180deg,
      rgba(255,255,255,0.02),
      transparent
    )
  `,
  border: "1px solid rgba(255,255,255,0.04)",
  borderRadius: "18px",
};

const titleSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  mb: 2,
};

const descSx = {
  maxWidth: "34rem",
  fontSize: "0.95rem",
  lineHeight: 1.8,
  color: colors.text.secondary,
  mb: { xs: 6, md: 8 },
};

const btnPrimarySx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.contrast,
  background: colors.primary.main,
  px: "32px",
  py: "14px",
  textDecoration: "none",
  clipPath:
    "polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)",
  border: "1px solid transparent",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s ease, border-color .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.12),
        transparent
      )
    `,
    transform: "translateX(-120%)",
    transition: "transform .6s ease",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: "rgba(255,255,255,0.18)",
    boxShadow:
      "0 10px 30px rgba(232,163,61,0.18)",
  },
  "&:hover::before": {
    transform: "translateX(120%)",
  },
  "&:active": {
    transform: "translateY(0px)",
  },
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "32px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), border-color .25s ease, color .25s ease, box-shadow .25s ease, background .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "15%",
    right: "15%",
    height: "1px",
    background:
      "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)",
    opacity: 0,
    transition: "opacity .25s ease",
  },
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.25)",
    background:
      "linear-gradient(180deg, rgba(232,163,61,0.04), rgba(232,163,61,0.02))",
    boxShadow:
      "0 8px 24px rgba(232,163,61,0.08)",
    transform: "translateY(-2px)",
  },
  "&:hover::before": {
    opacity: 1,
  },
  "&:active": {
    transform: "translateY(0px)",
  },
};

const GOLD_GRADIENT = `
  linear-gradient(
    180deg,
    #f7cb79 0%,
    #f0b458 40%,
    #de8f2c 100%
  )
`;

const TIERS = [
  { key: "elite", name: "Elite", range: "90–100", desc: "Consistent excellence across every dimension.", barWidth: "100%" },
  { key: "high", name: "High", range: "75–89", desc: "A reliable player others can trust to show up.", color: "#C0C0C0", barWidth: "78%" },
  { key: "good", name: "Good", range: "50–74", desc: "Solid standing. Room to keep improving.", color: "#6A9A6A", barWidth: "55%" },
  { key: "fair", name: "Fair", range: "30–49", desc: "Issues in your record. Patterns are visible.", color: "#7A8A9A", barWidth: "35%" },
  { key: "low", name: "Low", range: "0–29", desc: "Significant history of negative conduct.", color: "#4A4A4A", barWidth: "15%" },
];

const DIMENSIONS = [
  {
    num: "01",
    name: "Sportsmanship",
    desc: "Fair play, respect for teammates and opponents, and keeping a positive attitude when the round isn't going your way.",
    tip: "Stay composed under pressure. Avoid venting in chat. The way you lose matters as much as whether you win.",
    icon: <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />,
  },
  {
    num: "02",
    name: "Integrity",
    desc: "A clean record. No cheating, no exploits, no third-party tools. The hardest dimension to rebuild once it breaks.",
    tip: "Play the game as intended. Integrity carries the most weight alongside Sportsmanship and Reliability.",
    icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  },
  {
    num: "03",
    name: "Communication",
    desc: "Helpful, timely callouts. Team coordination over noise. The ability to talk in a way that actually helps people win.",
    tip: "Share useful information. Respond to callouts. Silence is better than toxicity.",
    icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  },
  {
    num: "04",
    name: "Team Play",
    desc: "Playing for the round, not the stat line. Supporting teammates, running coordinated strategies, and making the right call even when it costs you a kill.",
    tip: "Prioritize objectives. Sacrifice kills for information. Be the person your team actually wants at the site.",
    icon: (<><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></>),
  },
  {
    num: "05",
    name: "Reliability",
    desc: "Finishing what you start. No disconnects, no AFK rounds, no walking out when things get hard. Your teammates agreed to a full match.",
    tip: "Complete every match you join. Reliability carries the same weight as Integrity and Sportsmanship.",
    icon: (<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>),
  },
  {
    num: "06",
    name: "Reporter Quality",
    desc: "Submitting accurate, well-evidenced reports that the admin team can actually act on. Reporting isn't free — it's a responsibility.",
    tip: "Only report genuine violations. Provide context. False reports hurt your own score.",
    icon: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>),
  },
];

const POSITIVE_SCORES = [
  { label: "Match completion", val: "+0.5 to +2.0 pts" },
  { label: "Clean streak (10 matches)", val: "+5 pts bonus" },
  { label: "Commendations from teammates", val: "+2 to +5 pts" },
  { label: "Validated report submitted", val: "+2 Reporter Quality" },
  { label: "False report received", val: "+2 compensation" },
];

const NEGATIVE_SCORES = [
  { label: "Validated report against you", val: "−5 to −20 pts" },
  { label: "Match abandonment", val: "−10 pts" },
  { label: "Dismissed report submitted", val: "−1 Reporter Quality" },
  { label: "False report submitted", val: "−5 to −20 Reporter Quality" },
];

const REPORT_STEPS = [
  { num: "01", text: <>You submit a report with a description and any evidence you have.</> },
  { num: "02", text: <>The report enters the admin review queue.</> },
  { num: "03", text: <>An admin reviews evidence, match data, and both players&apos; histories.</> },
  { num: "04", text: <>A decision is reached: validated, dismissed, or false report.</> },
  { num: "05", text: <>Reputation adjustments apply to both players based on the outcome.</> },
];

const OUTCOMES = [
  { badge: "Validated", type: "validated", reporter: "+2 Reporter Quality", reported: "−5 to −20 by severity" },
  { badge: "Dismissed", type: "dismissed", reporter: "−1 Reporter Quality", reported: "No penalty" },
  { badge: "False Report", type: "false", reporter: "−5 to −20 Reporter Quality", reported: "+2 compensation" },
];

const FAQ = [
  { q: "Does my reputation decay over time?", a: "No. Reputation is cumulative and permanent. Scores change only because of what you do — not because of how long ago you did it." },
  { q: "Can other players see my detailed history?", a: "Other Ouro members can see your reputation breakdown when viewing your profile. Your personal history and improvement tips stay completely private." },
  { q: "What if I'm falsely reported?", a: "Every report goes through admin review before anything changes. If a report is found to be false, the reporter takes the penalty and you receive a compensation bonus." },
  { q: "Can I reset my reputation?", a: "No. Reputation is permanent and tied to your account. Accountability isn't something you can walk away from by starting a new profile." },
  { q: "How do I improve my score?", a: "Finish matches, communicate constructively, stay out of reports, and commend teammates who earn it. Your dashboard surfaces specific tips for each dimension based on your actual history." },
  { q: "Which dimensions carry the most weight?", a: "Integrity, Sportsmanship, and Reliability carry the highest weight. They have the biggest direct impact on whether your teammates have a good match experience." },
];

const RECORD_POINTS = [
  { label: "Starting point", body: "Every new player begins at 50 across all six dimensions. Neutral. Clean. From here it's entirely up to you." },
  { label: "No decay", body: "Scores don't fade with time. They change because of what you do — not because of how long ago you did it." },
  { label: "Six dimensions", body: "Each area of conduct is tracked independently. Being a great communicator doesn't hide a history of abandoning matches." },
  { label: "Permanent", body: "Reputation is tied to your account. You can't escape accountability by starting over." },
];

function tierColor(tier) {
  return tier.key === "elite" ? colors.primary.main : tier.color;
}

function Eyebrow({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2 }}>
      <Box sx={{ width: 24, height: "1px", background: colors.primary.hover }} />
      <Typography
        sx={{
          fontSize: { xs: "0.7rem", md: "0.75rem" },
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: colors.primary.hover,
          textTransform: "uppercase",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SectionIntro({ label, title, description }) {
  return (
    <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
      <Eyebrow>{label}</Eyebrow>
      <Typography component="h2" sx={titleSx}>{title}</Typography>
      {description ? <Typography sx={descSx}>{description}</Typography> : null}
    </MotionBox>
  );
}

function ScoreColumnHeader({ symbol, title, tone }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 20,
          height: 20,
          display: "grid",
          placeItems: "center",
          border: `1px solid ${tone}`,
          borderRadius: "2px",
          fontSize: "15px",
          fontWeight: 600,
          lineHeight: 1,
          color: tone,
        }}
      >
        {symbol}
      </Box>
      <Typography
        sx={{
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: "13px",
          fontWeight: 700,
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          color: tone,
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

function OutcomeBadge({ type, label }) {
  const styles = {
    validated: { bg: "rgba(34,197,94,0.12)", color: colors.state.success.main },
    dismissed: { bg: "rgba(115,115,125,0.2)", color: colors.text.muted },
    false: { bg: "rgba(220,38,38,0.12)", color: colors.state.danger.main },
  };
  const style = styles[type];

  return (
    <Box
      component="span"
      sx={{
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        px: 1,
        py: 0.5,
        display: "inline-block",
        background: style.bg,
        color: style.color,
      }}
    >
      {label}
    </Box>
  );
}

export default function ReputationPage() {
  return (
    <Box sx={{ backgroundColor: HERO_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <Box component="header" sx={heroSectionSx}>
        <DarkHeroPageEffects watermark="REPUTATION" />

        <Box sx={{ position: "relative", zIndex: 2, maxWidth: "75rem", mx: "auto", width: "100%" }}>
          <MotionBox {...HERO_ENTER} transition={{ ...REVEAL_TRANSITION, delay: 0 }}>
            <Eyebrow>Reputation System</Eyebrow>
          </MotionBox>

          <MotionBox
            component="h1"
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.1 }}
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: { xs: "2.25rem", sm: "2.5rem", md: "3.25rem", lg: "3.5rem", xl: "3.75rem" },
              fontWeight: 700,
              letterSpacing: "-0.075em",
              textTransform: "uppercase",
              lineHeight: 1,
              color: colors.text.primary,
              maxWidth: 900,
              mb: 4,
            }}
          >
            Know who you&apos;re
            <br />
            queueing{" "}
            <Box
              component="span"
              sx={{
                background: GOLD_GRADIENT,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              with.
            </Box>
          </MotionBox>

          <MotionBox {...HERO_ENTER} transition={{ ...REVEAL_TRANSITION, delay: 0.2 }}>
            <Typography
              sx={{
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 500,
                letterSpacing: "0.01em",
                lineHeight: 1.65,
                color: "rgba(255,255,255,0.72)",
                maxWidth: "34rem",
                mb: 6,
              }}
            >
              A lifetime record of how you play. Not just whether you won —
              how you treated your teammates, finished your matches, and used the tools you were given.
            </Typography>
          </MotionBox>

          <MotionBox
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.3 }}
            sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join the Founding Community
            </Box>
            <Box component={Link} to="/how-it-works" sx={btnGhostSx}>
              How Ouro works →
            </Box>
          </MotionBox>
        </Box>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="How it works"
            title="The long game."
            description="Reputation isn't a per-match score. It's a cumulative record that builds — or decays — with every decision you make on Ouro. One bad game doesn't define you. But patterns do."
          />
          <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}>
            <Box
              sx={{
                ...cardSx,
                p: { xs: 4, md: 6 },
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "auto 1fr" },
                gap: { xs: 4, md: 6 },
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    color: colors.text.muted,
                    mb: 1,
                  }}
                >
                  Starting score
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: { xs: "4.5rem", md: "6rem" },
                    fontWeight: 700,
                    color: colors.text.primary,
                    lineHeight: 1,
                  }}
                >
                  50
                </Typography>
                <Typography sx={{ fontSize: "1.125rem", color: colors.text.muted }}>/ 100</Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 3 }}>
                {RECORD_POINTS.map((point) => (
                  <Box key={point.label}>
                    <Typography sx={{ fontSize: "0.8125rem", color: colors.text.muted, mb: 0.75 }}>
                      {point.label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.9375rem", color: colors.text.secondary, lineHeight: 1.6 }}>
                      {point.body}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Trust Tiers"
            title="Where do you stand?"
            description="Tiers translate your score into something readable at a glance. The number is always there — but the tier tells the story."
          />
          <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" },
                gap: { xs: 1.5, md: 2 },
                mt: 7,
              }}
            >
              {TIERS.map((tier, index) => {
                const accent = tierColor(tier);
                return (
                  <MotionBox
                    key={tier.key}
                    {...REVEAL}
                    transition={{ ...REVEAL_TRANSITION, delay: 0.08 * index }}
                    sx={{
                      ...cardSx,
                      p: { xs: "24px 16px", md: "32px 24px" },
                      position: "relative",
                      overflow: "hidden",
                      transition: "border-color 0.3s",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: accent,
                      },
                      "&:hover": { borderColor: colors.border.focus },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        letterSpacing: "0.2em",
                        textTransform: "uppercase",
                        color: accent,
                        mb: 2,
                      }}
                    >
                      {tier.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: "2.25rem",
                        fontWeight: 700,
                        color: colors.text.primary,
                        lineHeight: 1,
                        mb: 1,
                      }}
                    >
                      {tier.range}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8125rem", color: colors.text.muted, lineHeight: 1.6 }}>
                      {tier.desc}
                    </Typography>
                    <Box sx={{ height: "2px", background: colors.background.surface, mt: 2.5 }}>
                      <Box sx={{ height: "100%", background: accent, width: tier.barWidth }} />
                    </Box>
                  </MotionBox>
                );
              })}
            </Box>

            <Box
              sx={{
                ...cardSx,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
                gap: { xs: 3, md: 5 },
                alignItems: "center",
                p: { xs: 4, md: 6 },
                mt: { xs: 6, md: 10 },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: colors.primary.main,
                    mb: 1.25,
                  }}
                >
                  Reputation
                </Typography>
                <Typography sx={{ fontSize: "0.9375rem", color: colors.text.secondary, lineHeight: 1.7 }}>
                  How you&apos;ve played across your entire time on Ouro. Reliability, sportsmanship, communication, and conduct — measured over hundreds of games, not just one.
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "3rem",
                  fontWeight: 700,
                  color: colors.text.muted,
                  textTransform: "uppercase",
                  textAlign: "center",
                  display: { xs: "none", md: "block" },
                }}
              >
                VS
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: colors.primary.main,
                    mb: 1.25,
                  }}
                >
                  Impact Rating
                </Typography>
                <Typography sx={{ fontSize: "0.9375rem", color: colors.text.secondary, lineHeight: 1.7 }}>
                  What you actually did in a single match. Duels won, trades taken, utility used, rounds played for the team. A snapshot of your in-game contribution — nothing more.
                </Typography>
              </Box>
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Six Dimensions"
            title="One score isn't enough."
            description="Conduct doesn't fit into a single number. Each dimension tracks something distinct — so you can be excellent in some areas and still see exactly where you need to grow."
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: { xs: 1.5, md: 2 },
              mt: 8,
            }}
          >
            {DIMENSIONS.map((dim, index) => (
              <MotionBox
                key={dim.num}
                {...REVEAL}
                transition={{ ...REVEAL_TRANSITION, delay: index * 0.08 }}
                sx={{
                  ...cardSx,
                  p: { xs: "32px 24px", md: "40px 36px" },
                  position: "relative",
                  overflow: "hidden",
                  transition: "background 0.3s, border-color 0.3s",
                  "&:hover": {
                    background: `
                      linear-gradient(
                        180deg,
                        rgba(255,255,255,0.04),
                        transparent
                      )
                    `,
                  },
                  "&:hover .dim-number": { color: colors.border.subtle },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "35%",
                    height: "1px",
                    zIndex: 1,
                    pointerEvents: "none",
                    background: `
                      linear-gradient(
                        90deg,
                        transparent,
                        rgba(246,185,76,0.9),
                        transparent
                      )
                    `,
                    boxShadow: "0 0 18px rgba(246,185,76,0.45)",
                  }}
                />
                <Typography
                  className="dim-number"
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "4rem",
                    fontWeight: 700,
                    color: colors.background.surface,
                    lineHeight: 1,
                    position: "absolute",
                    top: 32,
                    right: 32,
                    transition: "color 0.3s",
                  }}
                >
                  {dim.num}
                </Typography>
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  sx={{ width: 32, height: 32, mb: 2, color: colors.primary.main }}
                >
                  {dim.icon}
                </Box>
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.125rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: colors.text.primary,
                    mb: 1.25,
                  }}
                >
                  {dim.name}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary, lineHeight: 1.7, mb: 2.5 }}>
                  {dim.desc}
                </Typography>
                <Box sx={{ pt: 2, borderTop: `1px solid ${colors.border.subtle}` }}>
                  <Typography
                    sx={{
                      fontSize: "0.625rem",
                      fontWeight: 700,
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      color: colors.primary.main,
                      mb: 0.5,
                    }}
                  >
                    How to improve
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: colors.text.muted, lineHeight: 1.6 }}>
                    {dim.tip}
                  </Typography>
                </Box>
              </MotionBox>
            ))}
          </Box>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Score Changes"
            title="How scores move."
            description="Reputation responds to patterns, not single events. Good behavior compounds. Bad behavior has consequences. Here's exactly what moves the needle."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 5,
              mt: 8,
            }}
          >
            {[
              { key: "pos", symbol: "+", title: "Positive signals", tone: colors.state.success.main, items: POSITIVE_SCORES },
              { key: "neg", symbol: "−", title: "Negative signals", tone: colors.state.danger.main, items: NEGATIVE_SCORES },
            ].map((col) => (
              <Box key={col.key}>
                <ScoreColumnHeader symbol={col.symbol} title={col.title} tone={col.tone} />
                {col.items.map((item, index) => (
                  <Box
                    key={item.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: 2,
                      py: "14px",
                      borderBottom: index === col.items.length - 1 ? "none" : `1px solid ${colors.border.subtle}`,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary }}>{item.label}</Typography>
                    <Typography
                      sx={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        color: col.tone,
                        flexShrink: 0,
                      }}
                    >
                      {item.val}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Reporting"
            title="Accountability, both ways."
            description="Every report is reviewed. Every outcome affects both the player who filed it and the player who received it. This keeps reporting sharp — and prevents it from becoming a weapon."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 5, md: 10 },
              mt: 8,
              alignItems: "start",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: colors.text.muted,
                  mb: 3,
                }}
              >
                Review process
              </Typography>
              {REPORT_STEPS.map((step, index) => (
                <Box
                  key={step.num}
                  sx={{
                    display: "flex",
                    gap: 2.5,
                    py: 3,
                    borderBottom: index === REPORT_STEPS.length - 1 ? "none" : `1px solid ${colors.border.subtle}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      color: colors.primary.main,
                      width: 24,
                      flexShrink: 0,
                      pt: "2px",
                    }}
                  >
                    {step.num}
                  </Typography>
                  <Typography sx={{ fontSize: "0.9375rem", color: colors.text.secondary, lineHeight: 1.6 }}>
                    {step.text}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: colors.text.muted,
                  mb: 3,
                }}
              >
                Outcome breakdown
              </Typography>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                <Box component="thead">
                  <Box component="tr" sx={{ borderBottom: `1px solid ${colors.border.focus}` }}>
                    {["Decision", "Reporter", "Reported player"].map((heading, i) => (
                      <Box
                        component="th"
                        key={heading}
                        sx={{
                          fontSize: "0.625rem",
                          fontWeight: 700,
                          letterSpacing: "0.15em",
                          textTransform: "uppercase",
                          color: colors.text.muted,
                          textAlign: "left",
                          pb: 1.5,
                          pl: i === 0 ? 0 : 2.5,
                        }}
                      >
                        {heading}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {OUTCOMES.map((row, index) => (
                    <Box
                      component="tr"
                      key={row.badge}
                      sx={{ borderBottom: index === OUTCOMES.length - 1 ? "none" : `1px solid ${colors.border.subtle}` }}
                    >
                      <Box component="td" sx={{ py: "14px", color: colors.text.secondary, verticalAlign: "top" }}>
                        <OutcomeBadge type={row.type} label={row.badge} />
                      </Box>
                      <Box component="td" sx={{ py: "14px", pl: 2.5, color: colors.text.secondary, verticalAlign: "top" }}>
                        {row.reporter}
                      </Box>
                      <Box component="td" sx={{ py: "14px", pl: 2.5, color: colors.text.secondary, verticalAlign: "top" }}>
                        {row.reported}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Privacy"
            title="Visible where it helps."
            description="Enough signal to make an informed decision before you queue. Enough privacy that personal history stays yours."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 1.5, md: 2 },
              mt: 8,
            }}
          >
            {[
              {
                key: "public",
                title: "Public — everyone can see",
                accent: colors.primary.main,
                items: ["Trust tier", "Overall trust score", "Reputation breakdown across all six dimensions", "Tier badge on your player profile"],
              },
              {
                key: "private",
                title: "Private — only visible to you",
                accent: colors.text.muted,
                items: ["Detailed reputation history and timeline", "Personalized improvement tips per dimension", "Your standing compared to platform averages"],
              },
            ].map((card) => (
              <Box
                key={card.key}
                sx={{
                  ...cardSx,
                  p: 5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: card.accent,
                    mb: 3.5,
                  }}
                >
                  {card.title}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {card.items.map((item) => (
                    <Typography
                      key={item}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        fontSize: "0.875rem",
                        color: colors.text.secondary,
                        "&::before": {
                          content: '""',
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: card.accent,
                        },
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro label="FAQ" title="Common questions." />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              mt: 8,
              border: `1px solid ${colors.border.subtle}`,
            }}
          >
            {FAQ.map((item, index) => (
              <Box
                key={item.q}
                sx={{
                  p: { xs: "28px 24px", md: "36px 40px" },
                  borderBottom: index === FAQ.length - 1 ? "none" : `1px solid ${colors.border.subtle}`,
                  borderRight: { xs: "none", md: index % 2 === 0 ? `1px solid ${colors.border.subtle}` : "none" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: colors.text.primary,
                    mb: 1.5,
                  }}
                >
                  {item.q}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary, lineHeight: 1.75 }}>
                  {item.a}
                </Typography>
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer
          sx={{
            maxWidth: "75rem",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
            alignItems: "center",
            gap: { xs: 5, md: 8 },
          }}
        >
          <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
            <Typography component="h2" sx={{ ...titleSx, mb: 2 }}>
              Reputation becomes
              <br />
              useful before
              <br />
              you queue.
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", color: colors.text.secondary, maxWidth: 400, lineHeight: 1.7 }}>
              Join the founding community and build a record that means something from day one.
            </Typography>
          </MotionBox>
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Box component={Link} to={{ pathname: "/", hash: "get-started" }} sx={btnPrimarySx}>
              Join founding community
            </Box>
            <Box component={Link} to="/how-it-works" sx={btnGhostSx}>
              How Ouro works →
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>
    </Box>
  );
}
